<template>
	<div class="splash-page">
		<header class="splash-header">
			<img
				:src="logoSrc"
				alt="Logo"
				class="logo"
			>
			<UserMenu
				:user-full-name="userFullName"
				@logout="logout"
			/>
		</header>
		<main
			v-if="!riskRedirectInProgress && !riskRedirectFailed && !privacyNoticesRedirectInProgress && !privacyNoticesRedirectFailed"
			class="splash-content"
		>
			<h1 class="splash-title">
				Welcome {{ userFullName }}
			</h1>
			<p class="splash-subtitle">
				Select the Syrenis product you'd like to access
			</p>
			<div class="service-grid">
				<OverviewCard
					title="Administration Portal"
					description="Manage users, system settings and core configuration"
					icon="mdi-cog-outline"
					@overview="goAdmin"
				/>
				<OverviewCard
					v-if="hasRiskPortalAccess"
					title="Risk Portal"
					description="Identify and manage your risk exposure"
					icon="mdi-shield-check-outline"
					@overview="handleRiskOverview"
				/>
				<OverviewCard
					v-if="hasPrivacyNoticesAccess"
					title="Privacy Notices"
					description="Create and manage privacy notices for your organization"
					icon="mdi-note-text-outline"
					@overview="handlePrivacyNoticesOverview"
				/>
			</div>
		</main>
		<main
			v-else-if="riskRedirectFailed"
			class="splash-content redirecting"
		>
			<h1 class="splash-title">
				Risk Portal Redirect Failed
			</h1>
			<p class="splash-subtitle">
				We couldn't complete the secure handoff. Please retry.
			</p>
			<SecondaryActionButton @click="riskRedirectWithRetry">
				Retry Redirect
			</SecondaryActionButton>
		</main>
		<main
			v-else-if="riskRedirectInProgress"
			class="splash-content redirecting"
		>
			<h1 class="splash-title">
				Redirecting to Risk Portal…
			</h1>
			<p class="splash-subtitle">
				Please wait while we connect you securely.
			</p>
			<div
				class="loader"
				aria-label="Redirecting"
			/>
		</main>
		<main
			v-else-if="privacyNoticesRedirectFailed"
			class="splash-content redirecting"
		>
			<h1 class="splash-title">
				Privacy Notices Redirect Failed
			</h1>
			<p class="splash-subtitle">
				We couldn't complete the secure handoff. Please retry.
			</p>
			<SecondaryActionButton @click="privacyNoticesRedirectWithRetry">
				Retry Redirect
			</SecondaryActionButton>
		</main>
		<main
			v-else-if="privacyNoticesRedirectInProgress"
			class="splash-content redirecting"
		>
			<h1 class="splash-title">
				Redirecting to Privacy Notices…
			</h1>
			<p class="splash-subtitle">
				Please wait while we connect you securely.
			</p>
			<div
				class="loader"
				aria-label="Redirecting"
			/>
		</main>
		<footer class="splash-footer">
			© {{ currentYear }} Syrenis. All rights reserved.
		</footer>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import logoLight from '../../assets/images/Syrenis-Black.svg'
import logoDark from '../../../../shared/assets/Syrenis-red.svg'
import { createExchangeTokenRedirect } from '../../services/exchangeTokenService.js'
import { TARGET_APPS } from '../../../../shared/enums/target-apps.js'
import { getDefaultAdminLandingRoute } from '../../utils/postLoginDestination.js'
import SecondaryActionButton from '../../../../shared/components/secondary-action-button.vue'
import OverviewCard from '../../../../shared/components/overview-card.vue'
import UserMenu from '../../../../shared/components/user-menu.vue'
import {
	hasRiskPortalAccess as checkRiskPortalAccess,
	hasPrivacyNoticesAccess as checkPrivacyNoticesAccess,
	isRiskPortalOnly,
	isPrivacyNoticesOnly
} from '../../../../shared/utils/product-access.js'
import { clearClientSessionArtifacts } from '../../state/modules/auth.js'
import { privacyNoticeModuleEnabled } from '../../../../shared/state/config-keys.js'
import { navigateToDashboardPortal } from '../../../../shared/utils/navigation/dashboard-navigation.js'

const CONSENT_BANNERS_DASHBOARD_ID = 0

export default {
	name: 'splash-page',
	components: {
		SecondaryActionButton,
		OverviewCard,
		UserMenu
	},
	setup () {
		return {
			privacyNoticeModuleEnabled
		}
	},
	data () {
		return {
			riskOnlyRedirectAttempted: false,
			riskRedirectInProgress: false,
			riskRedirectFailed: false,
			privacyNoticesOnlyRedirectAttempted: false,
			privacyNoticesRedirectInProgress: false,
			privacyNoticesRedirectFailed: false
		}
	},
	computed: {
		...mapGetters('auth', ['userFullName', 'userDefaultDashboardId', 'productAreaPermission']),
		logoSrc () {
			return this.$vuetify.theme.dark ? logoDark : logoLight
		},
		/**
		 * Check if user has at least one Risk Portal permission
		 * @returns {boolean} True if user has any risk portal permission
		 */
		hasRiskPortalAccess () {
			return checkRiskPortalAccess(this.productAreaPermission)
		},
		/**
		 * Check if user has Privacy Notices access (config key enabled AND has permission)
		 * @returns {boolean} True if user can access Privacy Notices
		 */
		hasPrivacyNoticesAccess () {
			return checkPrivacyNoticesAccess(this.productAreaPermission, this.privacyNoticeModuleEnabled)
		},
		/**
		 * Returns true if user ONLY has Risk Portal permissions (no other permissions)
		 * Used for auto-redirect logic
		 * @returns {boolean} True if user only has Risk Portal access
		 */
		userIsRiskOnly () {
			const perms = this.$store.state.auth.userDetails?.productPermissions || []
			return isRiskPortalOnly(perms)
		},
		/**
		 * Returns true if user ONLY has Privacy Notices permissions (no other permissions)
		 * AND the privacy notice module is enabled. Used for auto-redirect logic
		 * @returns {boolean} True if user only has Privacy Notices access
		 */
		userIsPrivacyNoticesOnly () {
			const perms = this.$store.state.auth.userDetails?.productPermissions || []
			return isPrivacyNoticesOnly(perms, this.privacyNoticeModuleEnabled)
		},
		currentYear () {
			return new Date().getFullYear()
		}
	},
	mounted () {
		// Auto-redirect risk-only users immediately (do not show splash interaction options)
		if (this.userIsRiskOnly && !this.riskOnlyRedirectAttempted) {
			this.riskOnlyRedirectAttempted = true
			this.riskRedirectInProgress = true
			// Clear admin portal session cookie so revisiting admin portal shows login again
			clearClientSessionArtifacts()
			// Establish a timeout fallback in case exchange token flow hangs or network stalls
			const timeoutMs = 10000
			let timeoutHandle = null
			const onTimeout = () => {
				this.riskRedirectFailed = true
				this.riskRedirectInProgress = false
			}
			try {
				timeoutHandle = setTimeout(onTimeout, timeoutMs)
				// Slight nextTick delay ensures DOM switches to redirect view before heavy work
				this.$nextTick(async () => {
					try {
						await this.goRisk()
						clearTimeout(timeoutHandle)
					} catch (e) {
						clearTimeout(timeoutHandle)
						this.riskRedirectInProgress = false
						this.riskRedirectFailed = true
					}
				})
			} catch (e) {
				if (timeoutHandle) clearTimeout(timeoutHandle)
				this.riskRedirectInProgress = false
				this.riskRedirectFailed = true
			}
		}
		// Auto-redirect privacy-notices-only users immediately (do not show splash interaction options)
		if (this.userIsPrivacyNoticesOnly && !this.privacyNoticesOnlyRedirectAttempted) {
			this.privacyNoticesOnlyRedirectAttempted = true
			this.privacyNoticesRedirectInProgress = true
			// Clear admin portal session cookie so revisiting admin portal shows login again
			clearClientSessionArtifacts()
			// Establish a timeout fallback in case exchange token flow hangs or network stalls
			const timeoutMs = 10000
			let timeoutHandle = null
			const onTimeout = () => {
				this.privacyNoticesRedirectFailed = true
				this.privacyNoticesRedirectInProgress = false
			}
			try {
				timeoutHandle = setTimeout(onTimeout, timeoutMs)
				// Slight nextTick delay ensures DOM switches to redirect view before heavy work
				this.$nextTick(async () => {
					try {
						await this.goPrivacyNotices()
						clearTimeout(timeoutHandle)
					} catch (e) {
						clearTimeout(timeoutHandle)
						this.privacyNoticesRedirectInProgress = false
						this.privacyNoticesRedirectFailed = true
					}
				})
			} catch (e) {
				if (timeoutHandle) clearTimeout(timeoutHandle)
				this.privacyNoticesRedirectInProgress = false
				this.privacyNoticesRedirectFailed = true
			}
		}
	},
	methods: {
		...mapActions('auth', ['logOut']),
		logout () {
			this.logOut()
		},
		async goAdmin () {
			// If user's default dashboard is consent banners, redirect to the dedicated consent banners app
			if (this.userDefaultDashboardId === CONSENT_BANNERS_DASHBOARD_ID) {
				await navigateToDashboardPortal()
				return
			}
			const target = getDefaultAdminLandingRoute() || '/dashboard'
			this.$router.push(target)
		},
		async goRisk () {
			const redirectUrl = await createExchangeTokenRedirect(TARGET_APPS.RISK)
			if (!redirectUrl) {
				throw new Error('risk_redirect_missing')
			}
			window.location.href = redirectUrl
		},
		async goPrivacyNotices () {
			const redirectUrl = await createExchangeTokenRedirect(TARGET_APPS.NOTICES)
			if (!redirectUrl) {
				throw new Error('privacy_notices_redirect_missing')
			}
			window.location.href = redirectUrl
		},
		async handlePrivacyNoticesOverview () {
			this.privacyNoticesRedirectFailed = false
			this.privacyNoticesRedirectInProgress = true
			await this.privacyNoticesRedirectWithRetry()
		},
		async privacyNoticesRedirectWithRetry () {
			this.privacyNoticesRedirectFailed = false
			this.privacyNoticesRedirectInProgress = true
			const timeoutMs = 10000
			const timeoutHandle = setTimeout(() => {
				this.privacyNoticesRedirectFailed = true
				this.privacyNoticesRedirectInProgress = false
			}, timeoutMs)
			try {
				await this.goPrivacyNotices()
				clearTimeout(timeoutHandle)
			} catch (e) {
				clearTimeout(timeoutHandle)
				this.privacyNoticesRedirectFailed = true
				this.privacyNoticesRedirectInProgress = false
			}
		},
		async handleRiskOverview () {
			this.riskRedirectFailed = false
			this.riskRedirectInProgress = true
			await this.riskRedirectWithRetry()
		},
		async riskRedirectWithRetry () {
			this.riskRedirectFailed = false
			this.riskRedirectInProgress = true
			clearClientSessionArtifacts()
			const timeoutMs = 10000
			const timeoutHandle = setTimeout(() => {
				this.riskRedirectFailed = true
				this.riskRedirectInProgress = false
			}, timeoutMs)
			try {
				await this.goRisk()
				clearTimeout(timeoutHandle)
			} catch (e) {
				clearTimeout(timeoutHandle)
				this.riskRedirectFailed = true
				this.riskRedirectInProgress = false
			}
		}
	}
}
</script>

<style scoped>
.splash-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--global-main-background-color);
  font-family: 'Inter', sans-serif;
}

.splash-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 3rem;
  background-color: var(--global-card-background, #fff);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.logo {
  height: 48px;
  width: auto;
}

.splash-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.splash-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--global-header-text-color);
  margin: 0 0 0.5rem 0;
  text-align: center;
}

.splash-subtitle {
  font-size: 1.125rem;
  color: var(--global-foreground);
  margin: 0 0 3rem 0;
  text-align: center;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1000px;
}

.splash-footer {
  padding: 1.5rem 2rem;
  text-align: center;
  color: var(--global-foreground);
  font-size: 0.875rem;
  background-color: var(--global-card-background, #fff);
  border-top: 1px solid var(--global-border-color);
}

@media (max-width: 768px) {
  .splash-header {
    padding: 1rem 1.5rem;
  }

  .splash-title {
    font-size: 2rem;
  }

  .service-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}
</style>
